import { useEffect, useState } from "react";
import { Download, Loader2, Send, Play, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/motion";
import { http } from "@/lib/api";
import { getTracking } from "@/lib/tracking";
import { googleLinks, downloadIcs } from "@/lib/calendar";
import { drawPass } from "@/lib/pass";
import { MEDIA } from "@/lib/content";

const Step = ({ n, title, text, children, testid }) => (
  <Reveal>
    <div className="grid gap-5 border-b border-line py-10 sm:grid-cols-[72px_1fr]" data-testid={testid}>
      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 font-serif text-2xl text-gold-light">{n}</div>
      <div>
        <h3 className="text-3xl text-cream sm:text-4xl">{title}</h3>
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-sand">{text}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  </Reveal>
);

export const CalendarStep = () => (
  <Step n="1" title="Salva le date nel calendario" text="Mercoledì 25 e giovedì 26 novembre, dalle 20:30 alle 22:30. Ti mandiamo comunque un promemoria prima di ogni serata." testid="ty-calendar-step">
    <div className="flex flex-wrap gap-2">
      {googleLinks().map((g, i) => (
        <a key={g.url} href={g.url} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid={`ty-google-cal-${i + 1}`}>
          <CalendarPlus className="h-4 w-4" /> Google · {g.label.split(" ").slice(1, 2)} nov
        </a>
      ))}
      <button type="button" onClick={downloadIcs} className="btn-ghost" data-testid="ty-apple-cal-btn">Apple / iPhone</button>
      <button type="button" onClick={downloadIcs} className="btn-ghost" data-testid="ty-outlook-cal-btn">Outlook</button>
    </div>
  </Step>
);

export const VideoStep = () => (
  <Step n="2" title="Guarda il messaggio di Valentina e Renata" text="Un minuto per sapere cosa vedrai e come prepararti al meglio." testid="ty-video-step">
    <div className="relative aspect-video max-w-xl overflow-hidden rounded-2xl border border-line bg-[radial-gradient(80%_80%_at_50%_30%,rgba(199,154,75,0.25),#120e08_70%)]">
      {MEDIA.welcomeVideo ? (
        <video src={MEDIA.welcomeVideo} controls playsInline className="h-full w-full object-cover" data-testid="ty-welcome-video" />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-sand">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold-light/50"><Play className="ml-1 h-6 w-6 fill-gold-light text-gold-light" /></span>
          <span className="text-sm">Video di benvenuto (60 secondi)</span>
        </div>
      )}
    </div>
  </Step>
);

export const QuestionStep = ({ sessionId }) => {
  const [q, setQ] = useState("");
  const [state, setState] = useState("idle");
  const send = async () => {
    if (q.trim().length < 3) {
      toast.error("Scrivi prima la tua domanda.");
      return;
    }
    setState("sending");
    try {
      await http.post("/questions", { session_id: sessionId, domanda: q, tracking: getTracking() });
      setState("sent");
      setQ("");
    } catch {
      setState("idle");
      toast.error("Invio non riuscito, riprova.");
    }
  };
  return (
    <Step n="3" title="Qual è il tuo dubbio più grande?" text="Scrivi la domanda che vorresti fare sul Natural Anti-Age o sulla crescita del tuo centro. Valentina e Renata risponderanno in diretta alle più frequenti." testid="ty-question-step">
      <label htmlFor="q" className="sl-label">La tua domanda</label>
      <textarea id="q" value={q} onChange={(e) => setQ(e.target.value)} rows={4} className="sl-input mt-2 max-w-2xl resize-none" placeholder="Es. Come propongo un percorso anti-age a una cliente che oggi fa solo la pulizia viso?" data-testid="ty-question-input" />
      <div className="mt-4 flex items-center gap-4">
        <button type="button" onClick={send} disabled={state === "sending"} className="btn-gold !py-3" data-testid="ty-question-send-btn">
          {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia la domanda
        </button>
        {state === "sent" && <span className="text-sm text-gold-light" data-testid="ty-question-sent">Domanda inviata. Grazie!</span>}
      </div>
    </Step>
  );
};

export const PassStep = ({ info }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    const c = document.createElement("canvas");
    drawPass(c, { centro: info.nome_centro, citta: info.citta, provincia: info.provincia }).then((url) => {
      if (alive) setSrc(url);
    });
    return () => { alive = false; };
  }, [info.nome_centro, info.citta, info.provincia]);

  return (
    <Step n="4" title="Il tuo pass per le storie" text="Fai sapere alle tue clienti che stai investendo nella tua formazione. Te lo mandiamo anche su WhatsApp, pronto da pubblicare." testid="ty-pass-step">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
        <div className="aspect-[9/16] w-48 overflow-hidden rounded-2xl border border-gold/40 bg-ink2 shadow-[0_30px_80px_-30px_rgba(199,154,75,0.5)] sm:w-56">
          {src ? <img src={src} alt="Anteprima del pass personalizzato" className="h-full w-full object-cover" data-testid="ty-pass-preview" /> : <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>}
        </div>
        <a href={src || undefined} download="pass-workshop-skinlovers.png" className={`btn-gold ${src ? "" : "pointer-events-none opacity-50"}`} data-testid="ty-pass-download-btn">
          <Download className="h-4 w-4" /> Scarica il pass
        </a>
      </div>
    </Step>
  );
};
